import { Shield, DollarSign, Building, Layers } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface DealSecuritySummary {
  totalValue: number;
  lvrPercentage: number | null;
  securityCount: number;
  facilityCount: number;
  encumbranceCount: number;
  totalEncumbranceValue: number;
}

interface DealSecuritiesSummaryProps {
  summary?: DealSecuritySummary;
  isLoading?: boolean;
}

// Format currency
const formatCurrency = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

// Format percentage
const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "-";
  return `${value.toFixed(1)}%`;
};

export function DealSecuritiesSummary({ summary, isLoading }: DealSecuritiesSummaryProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="p-3">
              <div className="h-10 animate-pulse bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Total Value</p>
              <p className="text-sm font-semibold">
                {formatCurrency(summary?.totalValue)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            <Building className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Deal LVR</p>
              <p
                className={`text-sm font-semibold ${
                  summary?.lvrPercentage && summary.lvrPercentage > 80
                    ? "text-orange-600"
                    : summary?.lvrPercentage && summary.lvrPercentage > 70
                    ? "text-yellow-600"
                    : "text-green-600"
                }`}
              >
                {formatPercentage(summary?.lvrPercentage)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Securities</p>
              <p className="text-sm font-semibold">{summary?.securityCount ?? 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Facilities</p>
              <p className="text-sm font-semibold">{summary?.facilityCount ?? 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
